import * as React from 'react';
import type { AxiosError } from 'axios';
import axios from 'axios';
import useSWR from 'swr';
import { useSnackbar } from 'notistack';
import {
  Box, Grid, Dialog,
} from '@material-ui/core';
import RepairRejectReasonLookup from './RepairRejectReasonLookup';
import RepairRejectReasonAdd from './RepairRejectReasonAdd';
import type IRepairRejectReason from './RepairRejectReasonInterfaces';

const RepairRejectReasons: React.FC<unknown> = () => {
  const [upsertingBin, setUpsertingRejectReason] = React.useState<boolean>(false);

  const { enqueueSnackbar } = useSnackbar();
  const fetcher = (url: string) => axios.get<IRepairRejectReason[]>(url)
    .then((response) => response.data)
    .catch((e: AxiosError) => {
      enqueueSnackbar(`There was the following error when fetching repair reject reasons:
        ${JSON.stringify(e?.response?.data)}`, { persist: false, variant: 'error' });
    });
  const { data, mutate } = useSWR(['/repairRejectReason/GetRepairRejectReasons'], (url) => fetcher(url));
  const [rejectReasonFormValues, setRejectReasonFormValues] = React.useState<IRepairRejectReason>({
    reason: null,
    isBroncaReason: false,
    station: null,
  });

  const [showModal, setShowModal] = React.useState(false);

  const [editRejectReasonFormValues, setEditRejectReasonFormValues] = React.useState<IRepairRejectReason>({
    reason: null,
    isBroncaReason: false,
    station: null,
  });

  const resetState = () => {
    setRejectReasonFormValues({
      reason: null,
      isBroncaReason: false,
      station: null,
    });
  };

  const handleAddRejectReasonBtnClick = () => {
    if (!rejectReasonFormValues.reason || !rejectReasonFormValues.station) {
      enqueueSnackbar('Please fill out all the fields for the repair reject reason you are trying to create', { persist: false, variant: 'error' });
    } else {
      setUpsertingRejectReason(true);
      axios.post<IRepairRejectReason>('/repairRejectReason/upsert', rejectReasonFormValues)
        .then(() => {
          enqueueSnackbar('Repair Reject Reason added.', { variant: 'success' });
          setUpsertingRejectReason(false);
          mutate().catch((e) => { enqueueSnackbar(e, { persist: false, variant: 'error' }); });
          resetState();
        })
        .catch((e: AxiosError) => {
          enqueueSnackbar(`There was the following error when adding repair reject reason:
        ${JSON.stringify(e?.response?.data)}`, { persist: false, variant: 'error' });
          setUpsertingRejectReason(false);
        });
    }
  };

  const handleEditBinBtnClick = () => {
    if (!editRejectReasonFormValues.reason || !editRejectReasonFormValues.station) {
      enqueueSnackbar('Please fill out all the fields for the repair reject reason you are trying to edit', { persist: false, variant: 'error' });
    } else {
      setUpsertingRejectReason(true);
      if (editRejectReasonFormValues?.id) {
        axios.post<IRepairRejectReason>('/repairRejectReason/upsert', editRejectReasonFormValues)
          .then(() => {
            enqueueSnackbar('Repair Reject Reason edited.', { variant: 'success' });
            setUpsertingRejectReason(false);
            mutate().catch((e) => { enqueueSnackbar(e, { persist: false, variant: 'error' }); });
            resetState();
            setShowModal(false);
          })
          .catch((e: AxiosError) => {
            enqueueSnackbar(`There was the following error when editing repair reject reason:
            ${JSON.stringify(e?.response?.data)}`, { persist: false, variant: 'error' });
            setUpsertingRejectReason(false);
          });
      } else {
        enqueueSnackbar('There is no repair reject reason selected to edit');
      }
    }
  };

  const handleDeleteButton = () => {
    if (editRejectReasonFormValues?.id) {
      axios.post<IRepairRejectReason>('/repairRejectReason/delete', { id: editRejectReasonFormValues.id })
        .then(() => {
          enqueueSnackbar('Repair Reject Reason deleted.', { variant: 'success' });
          mutate().catch((e) => { enqueueSnackbar(e, { persist: false, variant: 'error' }); });
        })
        .catch((e: AxiosError) => {
          enqueueSnackbar(`There was the following error when deleting Repair Reject Reason:
        ${JSON.stringify(e?.response?.data)}`, { persist: false, variant: 'error' });
        });
    } else {
      enqueueSnackbar('There is no Repair Reject Reason selected to delete');
    }
  };

  const editCancelButton = () => {
    setEditRejectReasonFormValues({
      reason: null,
      station: null,
      isBroncaReason: false,
    });
    setShowModal(false);
  };

  return (
    <>
      <Grid xs={12} md={8}>
        <Grid item>
          <Box py={1}>
            <RepairRejectReasonAdd
              formValues={rejectReasonFormValues}
              setFormValues={setRejectReasonFormValues}
              secondaryButtonAction={resetState}
              primaryButtonAction={handleAddRejectReasonBtnClick}
              interfaceType="Add"
              loading={upsertingBin}
            />
          </Box>
        </Grid>
        <Grid item>
          <Box py={1}>
            <RepairRejectReasonLookup
              data={data || undefined}
              setShowModal={setShowModal}
              setEditRejectReasonFormValues={setEditRejectReasonFormValues}
              deleteRejectReason={handleDeleteButton}
            />
          </Box>
        </Grid>
        <Dialog
          onClose={editCancelButton}
          open={showModal}
          fullWidth
          maxWidth="md"
        >
          <RepairRejectReasonAdd
            formValues={editRejectReasonFormValues}
            setFormValues={setEditRejectReasonFormValues}
            secondaryButtonAction={editCancelButton}
            primaryButtonAction={handleEditBinBtnClick}
            interfaceType="Edit"
            loading={upsertingBin}
          />
        </Dialog>
      </Grid>
    </>
  );
};

export default RepairRejectReasons;
